import React, { useState, useEffect } from 'react';
import { auth, firestore as db } from '../../services/firebase';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';

interface DebugEntry {
  id: string;
  language: string;
  timestamp: any;
  request: any;
  response: any;
  apiVersion: string;
}

interface JobState {
  id: string;
  language: string;
  jobName: string;
  state: string;
  error: any;
  fullJob: any;
  timestamp: any;
}

interface JobDebugPanelProps {
  jobId: string;
  refreshTrigger?: number;
}

const JobDebugPanel: React.FC<JobDebugPanelProps> = ({ jobId, refreshTrigger }) => {
  const [debugEntries, setDebugEntries] = useState<DebugEntry[]>([]);
  const [jobStates, setJobStates] = useState<JobState[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'requests' | 'states'>('requests');
  const [selectedEntry, setSelectedEntry] = useState<DebugEntry | JobState | null>(null);

  useEffect(() => {
    fetchDebugData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, refreshTrigger]);

  const fetchDebugData = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Fetch debug entries
      const debugQuery = query(
        collection(db, 'jobs', jobId, 'debug'),
        orderBy('timestamp', 'desc')
      );
      const debugSnapshot = await getDocs(debugQuery);
      const debugData = debugSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as DebugEntry[];
      setDebugEntries(debugData);

      // Fetch job states
      const statesQuery = query(
        collection(db, 'jobs', jobId, 'jobStates'),
        orderBy('timestamp', 'desc')
      );
      const statesSnapshot = await getDocs(statesQuery);
      const statesData = statesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as JobState[];
      setJobStates(statesData);
    } catch (error) {
      console.error('Error fetching debug data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    if (timestamp._seconds) {
      const date = new Date(timestamp._seconds * 1000);
      return date.toLocaleString();
    }
    if (timestamp.seconds) {
      const date = new Date(timestamp.seconds * 1000);
      return date.toLocaleString();
    }
    return String(timestamp);
  };

  const copyToClipboard = (data: any) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    alert('Copied to clipboard!');
  };

  if (loading) {
    return <div className="p-4">Loading debug information...</div>;
  }

  return (
    <div className="bg-gray-100 rounded-lg p-6 mt-6">
      <h3 className="text-lg font-semibold mb-4">Debug Information</h3>
      
      <div className="mb-4">
        <button
          onClick={() => setActiveTab('requests')}
          className={`px-4 py-2 mr-2 rounded ${
            activeTab === 'requests' 
              ? 'bg-blue-600 text-white' 
              : 'bg-white text-gray-700 hover:bg-gray-200'
          }`}
        >
          API Requests ({debugEntries.length})
        </button>
        <button
          onClick={() => setActiveTab('states')}
          className={`px-4 py-2 rounded ${
            activeTab === 'states' 
              ? 'bg-blue-600 text-white' 
              : 'bg-white text-gray-700 hover:bg-gray-200'
          }`}
        >
          Job States ({jobStates.length})
        </button>
      </div>

      <div className="bg-white rounded-lg p-4">
        {activeTab === 'requests' ? (
          <div>
            {debugEntries.length === 0 ? (
              <p className="text-gray-500">No API requests logged yet</p>
            ) : (
              <div>
                <div className="space-y-2 mb-4">
                  {debugEntries.map((entry) => (
                    <div
                      key={entry.id}
                      onClick={() => setSelectedEntry(entry)}
                      className="p-3 border rounded cursor-pointer hover:bg-gray-50"
                    >
                      <div className="flex justify-between">
                        <span className="font-medium">{entry.language}</span>
                        <span className="text-sm text-gray-500">
                          {formatTimestamp(entry.timestamp)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        API Version: {entry.apiVersion}
                      </div>
                    </div>
                  ))}
                </div>
                
                {selectedEntry && 'request' in selectedEntry && (
                  <div className="mt-4 border-t pt-4">
                    <h4 className="font-semibold mb-2">Selected Request Details</h4>
                    
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <h5 className="font-medium">Request:</h5>
                        <button
                          onClick={() => copyToClipboard(selectedEntry.request)}
                          className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                        >
                          Copy Request
                        </button>
                      </div>
                      <pre className="bg-gray-100 p-3 rounded overflow-x-auto text-xs">
                        {JSON.stringify(selectedEntry.request, null, 2)}
                      </pre>
                    </div>
                    
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <h5 className="font-medium">Response:</h5>
                        <button
                          onClick={() => copyToClipboard(selectedEntry.response)}
                          className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                        >
                          Copy Response
                        </button>
                      </div>
                      <pre className="bg-gray-100 p-3 rounded overflow-x-auto text-xs">
                        {JSON.stringify(selectedEntry.response, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div>
            {jobStates.length === 0 ? (
              <p className="text-gray-500">No job states logged yet</p>
            ) : (
              <div>
                <div className="space-y-2 mb-4">
                  {jobStates.map((state) => (
                    <div
                      key={state.id}
                      onClick={() => setSelectedEntry(state)}
                      className="p-3 border rounded cursor-pointer hover:bg-gray-50"
                    >
                      <div className="flex justify-between">
                        <span className="font-medium">{state.language}</span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          state.state === 'SUCCEEDED' 
                            ? 'bg-green-100 text-green-800'
                            : state.state === 'FAILED'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {state.state}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatTimestamp(state.timestamp)}
                      </div>
                      {state.error && (
                        <div className="text-sm text-red-600 mt-1">
                          Error: {state.error.message || 'Unknown error'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {selectedEntry && 'fullJob' in selectedEntry && (
                  <div className="mt-4 border-t pt-4">
                    <h4 className="font-semibold mb-2">Full Job Details</h4>
                    
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <h5 className="font-medium">Job Name: {selectedEntry.jobName}</h5>
                        <button
                          onClick={() => copyToClipboard(selectedEntry.fullJob)}
                          className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                        >
                          Copy Full Job
                        </button>
                      </div>
                      <pre className="bg-gray-100 p-3 rounded overflow-x-auto text-xs">
                        {JSON.stringify(selectedEntry.fullJob, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobDebugPanel;